import { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import type { FilterMenuOption } from '@goodfynd/ui';
import type { IconName } from '@goodfynd/ui';

import config from '../../../config';
import strings from '../../../config/strings';
import { useLocation } from '../../../context/location-context';
import { DEFAULT_SEARCH_ROWS } from '../../../context/search-context/SearchContext';
import { useVendorApi } from '../../../hooks/api';
import type { VendorSearchResponse } from '../../../services/api/types';
import { useStores } from '../../../stores';
import type { VendorScheduleRequest } from '../../../types/vendors';
import { dateUtil } from '../../../utils';
import type { VendorSearchOptions } from '../types';

export default function useOpenVendorsSearch({ phrase }: VendorSearchOptions) {
  const api = useVendorApi();
  const { position } = useLocation();
  const {
    searchStore: { display, setDisplay },
  } = useStores();

  const today = dateUtil.startOfDay(new Date());
  const dates: Date[] = [...Array(6)].map((item, index) =>
    dateUtil.addDays(today, index, true)
  );

  const dayOptions: FilterMenuOption[] = dates.map((date) => ({
    label: `${strings.labels.open} ${dateUtil.getDayDisplay(
      date,
      config.dateFormats.weekDayMonthDay
    )}`,
    value: dateUtil.format(date, config.dateFormats.yearMonthDay),
  }));

  const [selected, setSelected] = useState(
    dayOptions?.[0] ? dayOptions[0].value : undefined
  );
  const [total, setTotal] = useState(0);

  /* Fetch open vendors */
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetched,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery<VendorSearchResponse, unknown>(
    [
      config.queryKeys.openVendors,
      `${position?.latitude},${position?.longitude}`,
      selected,
    ],
    ({ pageParam = 1 }) => {
      const request: VendorScheduleRequest = {
        date: selected,
        page: pageParam,
        query: phrase,
        rows: DEFAULT_SEARCH_ROWS,
      };

      if (Boolean(position?.latitude) && Boolean(position?.longitude)) {
        request.coordinates = `@${position?.latitude},${position?.longitude}`;
      }

      return api.getOpenVendors(request);
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage.hasMore ? lastPage.page + 1 : undefined,
      onSuccess: (data) => {
        const pageData = data.pages.at(-1);
        if (!pageData) {
          return;
        }

        setTotal(pageData?.total);
      },
    }
  );

  const vendors = useMemo(
    () => data?.pages?.flatMap((page) => page.results) || [],
    [data]
  );

  const getDisplayIcon = useCallback<() => IconName>(() => {
    switch (display) {
      case 'grid':
        return 'map';

      case 'list':
        return 'grid';

      case 'map':
        return 'grid';

      default:
        return 'map';
    }
  }, [display]);

  const toggleDisplayIcon = useCallback(() => {
    switch (display) {
      case 'map':
        setDisplay('grid');
        break;

      case 'grid':
      default:
        setDisplay('map');
        break;
    }
  }, [display, setDisplay]);

  return {
    dayOptions,
    fetchNextPage,
    getDisplayIcon,
    hasNextPage,
    isFetched,
    isFetchingNextPage,
    isLoading,
    pages: data?.pages,
    refetch,
    selected,
    setSelected,
    toggleDisplayIcon,
    total,
    vendors,
  };
}
